import { useEffect, useState } from 'react';
import { NB, ChoicePill } from '@cerebral-inc/ui';
import { useQuery } from 'react-query';
import { createLog } from 'utils/logging';
import { BasicSpinner } from 'components/shared/spinner';
import { BIPOLAR, DEPRESSION } from 'components/shared/types';
import { ADHD_ASSESSMENT_RESULT } from 'constants/localStorageKey';
import { AssessmentButton } from '../shared/button';
import ProgressBarComponent from './progress-bar';
import { AssessmentFlowProps, IAnswerOption, IQuestion } from './types';
import { clearAssessmentData, getWidgetLSKey } from '../shared/utils';
import CreatingScore from './creating-score';
import { segmentIntegrations } from '../shared/analytics/segment';
import EmergencyHelp from './emergency-help';

const { Center, Text, Heading, Flex, Box } = NB;

export const AssessmentFlow = ({
  serviceLine,
  handleNextPage,
  segmentEventName,
  storageKey = ADHD_ASSESSMENT_RESULT,
  loadingText = 'Just a few more minutes while we gather your results'
}: AssessmentFlowProps) => {
  const [assessmentResult, setAssessmentResult] = useState<IQuestion[]>([]);
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [stepPercent, setStepPercent] = useState<number>(0);
  const [isCreatingScore, setIsCreatingScore] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<
    IAnswerOption | undefined
  >();
  const [currentQuestion, setCurrentQuestion] = useState<IQuestion>(
    {} as IQuestion
  );
  const [shouldShowEmergencyHelp, setShouldShowEmergencyHelp] =
    useState<boolean>(false);

  const fetchQuestions = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_ASSESSMENT_API}/free-assessment?serviceLine=${serviceLine}`
    );
    return res.json();
  };

  const { isFetching } = useQuery(['fetchQuestions'], fetchQuestions, {
    enabled: questions.length === 0,
    onSuccess: (res: IQuestion[]) => {
      res.sort((a, b) => a.id - b.id);
      let remainingQuestions = res;
      setQuestions(res);

      const answeredQuestions =
        localStorage.getItem(getWidgetLSKey(storageKey)) || '';
      if (answeredQuestions !== '') {
        const answeredQuestionArr = JSON.parse(
          answeredQuestions
        ) as IQuestion[];
        const answeredQuestionIdArr = answeredQuestionArr.map(
          question => question.id
        );

        remainingQuestions = res.filter(
          item => !answeredQuestionIdArr.includes(item.id)
        );
        setAssessmentResult(answeredQuestionArr);
      }

      setCurrentQuestion(remainingQuestions[0]);
    },
    onError: error => {
      createLog({ message: `${error}`, status: 'error' });
    }
  });

  useEffect(() => {
    clearAssessmentData();
  }, []);

  useEffect(() => {
    if (!currentQuestion) {
      window.location.reload();
    }
  }, [currentQuestion]);

  useEffect(() => {
    const currentIndex =
      questions.findIndex(question => question.id === currentQuestion.id) + 1;
    setStepPercent((currentIndex / questions.length) * 100);
  }, [currentQuestion, questions]);

  useEffect(() => {
    setTimeout(() => {
      if (isCreatingScore) {
        handleNextPage();
      }
    }, 2000);
  }, [isCreatingScore, handleNextPage]);

  const handleContinue = () => {
    const currentIndex = questions.findIndex(
      question => question.id === currentQuestion.id
    );

    if (
      serviceLine === DEPRESSION &&
      currentIndex === questions.length - 1 &&
      selectedOption?.score === 3 &&
      !shouldShowEmergencyHelp
    ) {
      setShouldShowEmergencyHelp(true);
      return;
    }

    if (segmentEventName) {
      window.analytics?.track(
        segmentEventName,
        { id: currentQuestion.id, title: currentQuestion.title },
        segmentIntegrations
      );
    }

    const answeredQuestion = {
      ...currentQuestion,
      selectedOption
    };

    const result = [...assessmentResult, answeredQuestion];
    setAssessmentResult(result);

    if (currentIndex === questions.length - 1) {
      localStorage.setItem(storageKey, JSON.stringify(result));
      setIsCreatingScore(true);
      return;
    }

    setCurrentQuestion(questions[currentIndex + 1]);
    setSelectedOption(undefined);
  };

  const setSelected = (option: IAnswerOption) => {
    setSelectedOption(option);
  };

  if (isFetching) {
    return (
      <Flex
        minH="calc(100dvh - 189px)"
        px="24px"
        justifyContent="center"
        alignItems="center"
      >
        <Flex>
          <BasicSpinner />
        </Flex>
      </Flex>
    );
  }

  if (isCreatingScore) {
    return <CreatingScore loadingText={loadingText} />;
  }

  return (
    <Center
      w="100%"
      px={shouldShowEmergencyHelp ? { base: '24px', lg: 0 } : {}}
      py={shouldShowEmergencyHelp ? { base: '32px', sm: '64px' } : {}}
    >
      {shouldShowEmergencyHelp ? (
        <EmergencyHelp />
      ) : (
        <Center w="100%">
          <ProgressBarComponent stepPercent={stepPercent} />
          <Flex
            minH={{ base: 'calc(100dvh - 189px)', md: 'auto' }}
            px={{ base: '24px', md: '0' }}
            alignItems="center"
            w={{ base: '100%', md: '480px' }}
          >
            <Center>
              {!!currentQuestion.description && (
                <Text
                  color="#1C1E21"
                  lineHeight="24px"
                  fontWeight="400"
                  fontSize="14px"
                  fontFamily="Poppins"
                  marginBottom="8px"
                  textAlign="center"
                  maxW={serviceLine === BIPOLAR ? '296px' : '100%'}
                >
                  {currentQuestion.description}
                </Text>
              )}

              <Heading
                fontSize="18px"
                fontWeight="600"
                lineHeight="24px"
                textAlign="center"
                color="#1C1E21"
                mb="32px"
                fontFamily="Poppins"
              >
                {currentQuestion.title}
              </Heading>
            </Center>
            <Flex
              direction="column"
              justify="center"
              maxWidth="488px"
              width="full"
              marginBottom="3"
            >
              {currentQuestion.answer_options?.map(option => (
                <Box mt="1" mb="1" key={option.title}>
                  <ChoicePill
                    onSelect={() => setSelected(option)}
                    choicePillKey={option.title}
                    choicePillType="single"
                    selected={selectedOption?.id === option.id}
                    labelText={option.title}
                    size="large"
                    // fullWidth
                  />
                </Box>
              ))}
            </Flex>
            <Box maxWidth="md" width="full" pt="2" />
          </Flex>
        </Center>
      )}
      <AssessmentButton
        text={shouldShowEmergencyHelp ? 'Show my results' : 'Continue'}
        onSubmit={handleContinue}
        isDisabled={!selectedOption}
        isStickyButton={!shouldShowEmergencyHelp}
      />
    </Center>
  );
};

export default AssessmentFlow;
